import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Loader2,
  Minus,
  TrendingDown,
  TrendingUp,
  Upload,
  Users,
  X,
} from "lucide-react";
import { useRef, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AppLayout } from "../components/layout/AppLayout.js";
import { Header } from "../components/layout/Header.js";
import { useAuth } from "../hooks/useAuth.js";
import {
  type SurveyDimKey,
  type SurveySummary,
  type SurveyUploadResult,
  surveyApi,
} from "../lib/api.js";

const CARD_DIMS: SurveyDimKey[] = [
  "personalbindung",
  "weiterempfehlung",
  "engagement",
  "belastung",
  "weiterentwicklung",
];

const DIM_SHORT: Record<SurveyDimKey, string> = {
  personalbindung: "Bindung",
  weiterempfehlung: "Empfehlung",
  engagement: "Engagement",
  belastung: "Belastung",
  weiterentwicklung: "Entwicklung",
};
// Feste Serien-Zuordnung aus den Design-Tokens (--chart-1..5). Die Reihenfolge
// bleibt konstant, damit eine Dimension beim Filtern nie die Farbe wechselt.
const TREND_COLORS: Record<SurveyDimKey, string> = {
  personalbindung: "var(--chart-1)",
  weiterempfehlung: "var(--chart-2)",
  engagement: "var(--chart-3)",
  belastung: "var(--chart-4)",
  weiterentwicklung: "var(--chart-5)",
};

const SCALE_COLORS = [
  "rgba(239,80,80,0.90)",
  "rgba(249,140,66,0.88)",
  "rgba(245,200,66,0.90)",
  "rgba(132,204,22,0.82)",
  "rgba(16,185,129,0.88)",
];

function pctColor(v: number | null): string {
  if (v === null) return "var(--bg-hover)";
  if (v >= 80) return "rgba(16,185,129,0.88)";
  if (v >= 70) return "rgba(132,204,22,0.82)";
  if (v >= 60) return "rgba(245,200,66,0.88)";
  if (v >= 45) return "rgba(249,140,66,0.88)";
  return "rgba(239,80,80,0.90)";
}

/** Differenz-Farbe (Heatmap): grün positiv, rot negativ, Intensität nach Betrag. */
function diffColor(v: number | null): string {
  if (v === null || v === 0) return "var(--bg-hover)";
  const a = Math.min(1, Math.abs(v) / 25) * 0.8 + 0.15;
  return v > 0 ? `rgba(16,185,129,${a})` : `rgba(239,80,80,${a})`;
}

/** Ampel-Farbe für einen Skalenwert 1–5 (Mitarbeiter-Heatmap). */
function scaleColor(v: number | null): string {
  if (v === null) return "var(--bg-hover)";
  if (v >= 4.5) return "rgba(16,185,129,0.88)";
  if (v >= 3.5) return "rgba(132,204,22,0.82)";
  if (v >= 2.5) return "rgba(245,200,66,0.88)";
  if (v >= 1.5) return "rgba(249,140,66,0.88)";
  return "rgba(239,80,80,0.90)";
}

/** Differenz-Farbe auf der 1–5-Skala (kleinere Beträge → andere Normierung). */
function scaleDiffColor(v: number | null): string {
  if (v === null || v === 0) return "var(--bg-hover)";
  const a = Math.min(1, Math.abs(v) / 2) * 0.8 + 0.15;
  return v > 0 ? `rgba(16,185,129,${a})` : `rgba(239,80,80,${a})`;
}

const fmtPct = (v: number | null): string => (v === null ? "–" : `${v}%`);
const fmtDiff = (v: number | null): string => (v === null ? "–" : v > 0 ? `+${v}` : `${v}`);
const fmtScale = (v: number | null): string =>
  v === null ? "–" : Number.isInteger(v) ? String(v) : v.toFixed(1);
const fmtScaleDiff = (v: number | null): string =>
  v === null ? "–" : v > 0 ? `+${v.toFixed(1)}` : v.toFixed(1);

export function Pulsumfrage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const queryClient = useQueryClient();
  const [round, setRound] = useState<string | undefined>(undefined);
  const [team, setTeam] = useState<string>("");
  const [mode, setMode] = useState<"liste" | "heatmap">("liste");
  const [openQid, setOpenQid] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["survey-summary", round, team],
    queryFn: () => surveyApi.getSummary(round, team || undefined),
    staleTime: 5 * 60_000,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["survey-summary"] });

  return (
    <AppLayout>
      <Header title="Pulsumfrage" subtitle="Quartalsweise Stimmung · 5 Dimensionen · vertraulich" />
      <div className="flex-1 overflow-y-auto px-6 py-5 page-enter">
        {/* Controls */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          {data?.available && data.rounds.length > 0 && (
            <select
              value={data.round}
              onChange={(e) => setRound(e.target.value)}
              className="px-3 py-1.5 rounded text-sm"
              style={{
                background: "var(--bg-card)",
                color: "var(--text-primary)",
                border: "1px solid var(--border)",
              }}
            >
              {data.rounds.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          )}
          {data?.available && (data.teams?.length ?? 0) > 0 && (
            <select
              value={team}
              onChange={(e) => setTeam(e.target.value)}
              className="px-3 py-1.5 rounded text-sm max-w-[16rem]"
              style={{
                background: "var(--bg-card)",
                color: "var(--text-primary)",
                border: "1px solid var(--border)",
              }}
            >
              <option value="">Alle Teams</option>
              {data.teams!.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          )}
          <div className="flex-1" />
          {data?.available && (
            <div
              className="flex rounded overflow-hidden"
              style={{ border: "1px solid var(--border)" }}
            >
              {(["liste", "heatmap"] as const).map((m) => (
                <button
                  type="button"
                  key={m}
                  onClick={() => setMode(m)}
                  className="px-3 py-1.5 text-sm font-medium capitalize"
                  style={{
                    background: mode === m ? "var(--accent)" : "transparent",
                    color: mode === m ? "#fff" : "var(--text-muted)",
                  }}
                >
                  {m === "liste" ? "Liste" : "Heatmap"}
                </button>
              ))}
            </div>
          )}
          {isAdmin && <UploadButton onDone={refresh} />}
        </div>

        {isLoading && (
          <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
            <Loader2 size={16} className="animate-spin" /> Lade Umfragedaten …
          </div>
        )}

        {!isLoading && !data?.available && <EmptyState isAdmin={isAdmin} />}

        {!isLoading && data?.available && mode === "liste" && (
          <ListeView data={data} onOpenQuestion={setOpenQid} />
        )}
        {!isLoading && data?.available && mode === "heatmap" && (
          <HeatmapView round={round} team={team || undefined} />
        )}
      </div>

      {openQid && (
        <QuestionDrawer
          qid={openQid}
          round={round}
          team={team || undefined}
          onClose={() => setOpenQid(null)}
        />
      )}
    </AppLayout>
  );
}

// ── Liste-Ansicht ───────────────────────────────────────────────────────────────

function ListeView({
  data,
  onOpenQuestion,
}: { data: SurveySummary; onOpenQuestion: (qid: string) => void }) {
  const [expanded, setExpanded] = useState<Set<SurveyDimKey>>(new Set());
  const ratePct =
    data.participationRate != null ? `${Math.round(data.participationRate * 100)}%` : "–";

  const toggle = (d: SurveyDimKey) =>
    setExpanded((s) => {
      const n = new Set(s);
      n.has(d) ? n.delete(d) : n.add(d);
      return n;
    });

  return (
    <>
      {/* Antwortrate + 5 Highlight-Karten */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        <div
          className="rounded-lg p-4"
          style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
        >
          <div
            className="flex items-center gap-1.5 text-xs mb-1"
            style={{ color: "var(--text-muted)" }}
          >
            <Users size={13} /> Antwortrate
          </div>
          <div className="text-2xl font-bold" style={{ color: "var(--text-primary)" }}>
            {ratePct}
          </div>
          <div className="text-[10px] mt-0.5" style={{ color: "var(--text-muted)" }}>
            {data.participantCount} von {data.denominator}
          </div>
        </div>
        {CARD_DIMS.map((key) => {
          const t = data.themes?.find((x) => x.dimension === key);
          if (!t) return null;
          return (
            <div
              key={key}
              className="rounded-lg p-4"
              style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
            >
              <div className="text-xs mb-1 truncate" style={{ color: "var(--text-muted)" }}>
                {t.label}
              </div>
              <div className="text-2xl font-bold" style={{ color: pctColor(t.zustimmung) }}>
                {fmtPct(t.zustimmung)}
              </div>
              <div className="text-[10px] mt-0.5" style={{ color: "var(--text-muted)" }}>
                Zustimmung
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabelle: Themen aufklappbar → Fragen */}
      <section
        className="rounded-lg p-4"
        style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr style={{ color: "var(--text-muted)" }}>
                <th className="text-left font-medium py-1.5 pr-3">Fragenthema</th>
                <th className="text-left font-medium py-1.5 px-2 w-56">Verteilung der Antworten</th>
                <th className="text-right font-medium py-1.5 px-2">Zustimmung</th>
                <th className="text-right font-medium py-1.5 px-2">Differenz zur Vorrunde</th>
                <th className="text-right font-medium py-1.5 px-2">Antwortrate</th>
                <th className="text-right font-medium py-1.5 pl-2">Kommentare</th>
              </tr>
            </thead>
            <tbody>
              {data.themes?.map((t) => {
                const isOpen = expanded.has(t.dimension);
                const multi = t.questions.length > 1;
                return (
                  <>
                    <tr
                      key={t.dimension}
                      style={{ borderTop: "1px solid var(--border)", cursor: "pointer" }}
                      onClick={() =>
                        multi ? toggle(t.dimension) : onOpenQuestion(t.questions[0]?.id ?? "")
                      }
                    >
                      <td
                        className="py-2 pr-3 font-semibold"
                        style={{ color: "var(--text-primary)" }}
                      >
                        <span className="inline-flex items-center gap-1">
                          {multi ? (
                            isOpen ? (
                              <ChevronDown size={14} />
                            ) : (
                              <ChevronRight size={14} />
                            )
                          ) : (
                            <span className="w-[14px]" />
                          )}
                          {t.label}{" "}
                          {multi && (
                            <span style={{ color: "var(--text-muted)" }}>
                              ({t.questions.length})
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="py-2 px-2">
                        <DistBar dist={t.distribution} />
                      </td>
                      <td
                        className="py-2 px-2 text-right font-bold"
                        style={{ color: pctColor(t.zustimmung) }}
                      >
                        {fmtPct(t.zustimmung)}
                      </td>
                      <td className="py-2 px-2 text-right">
                        <DiffCell diff={t.diffToPrev} />
                      </td>
                      <td className="py-2 px-2 text-right" style={{ color: "var(--text-muted)" }}>
                        {data.participationRate != null
                          ? `${Math.round(data.participationRate * 100)}%`
                          : "–"}
                      </td>
                      <td
                        className="py-2 pl-2 text-right"
                        style={{ color: "var(--text-secondary)" }}
                      >
                        {t.commentCount}
                      </td>
                    </tr>
                    {(isOpen || !multi) &&
                      multi &&
                      t.questions.map((q) => (
                        <tr
                          key={q.id}
                          style={{ borderTop: "1px solid var(--border)", cursor: "pointer" }}
                          onClick={() => onOpenQuestion(q.id)}
                          className="hover:opacity-90"
                        >
                          <td className="py-2 pr-3 pl-6" style={{ color: "var(--text-secondary)" }}>
                            {q.text}
                          </td>
                          <td className="py-2 px-2">
                            <DistBar dist={q.distribution} />
                          </td>
                          <td
                            className="py-2 px-2 text-right font-semibold"
                            style={{ color: pctColor(q.zustimmung) }}
                          >
                            {fmtPct(q.zustimmung)}
                          </td>
                          <td className="py-2 px-2 text-right">
                            <DiffCell diff={q.diffToPrev} />
                          </td>
                          <td
                            className="py-2 px-2 text-right"
                            style={{ color: "var(--text-muted)" }}
                          >
                            {data.participationRate != null
                              ? `${Math.round(data.participationRate * 100)}%`
                              : "–"}
                          </td>
                          <td
                            className="py-2 pl-2 text-right"
                            style={{ color: "var(--text-secondary)" }}
                          >
                            {q.commentCount}
                          </td>
                        </tr>
                      ))}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] mt-2" style={{ color: "var(--text-muted)" }}>
          Klick auf eine Frage öffnet die Detailansicht mit Verteilung und Kommentaren. Zustimmung =
          Anteil der 4-/5-Antworten.
        </p>
      </section>

      {/* Trendlinie je Dimension (ab Runde 2) */}
      {(data.trend?.length ?? 0) >= 2 && (
        <section
          className="rounded-lg p-4 mt-6"
          style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
        >
          <h2 className="text-sm font-bold mb-3" style={{ color: "var(--text-primary)" }}>
            Zustimmung je Dimension über die Quartale
          </h2>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.trend!.map((p) => ({ round: p.round, ...p.dims }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="round" tick={{ fill: "var(--text-muted)", fontSize: 12 }} />
              <YAxis domain={[0, 100]} tick={{ fill: "var(--text-muted)", fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  background: "var(--bg-card)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  color: "var(--text-primary)",
                }}
              />
              <Legend />
              {CARD_DIMS.map((k) => (
                <Line
                  key={k}
                  type="monotone"
                  dataKey={k}
                  name={DIM_SHORT[k]}
                  stroke={TREND_COLORS[k]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </section>
      )}
    </>
  );
}

function DiffCell({ diff }: { diff: number | null }) {
  if (diff === null) return <span style={{ color: "var(--text-muted)" }}>–</span>;
  const color =
    diff > 0 ? "var(--risk-low-text)" : diff < 0 ? "var(--risk-high-text)" : "var(--text-muted)";
  const Icon = diff > 0 ? TrendingUp : diff < 0 ? TrendingDown : Minus;
  return (
    <span className="inline-flex items-center justify-end gap-1 font-medium" style={{ color }}>
      <Icon size={13} />
      {diff > 0 ? `+${diff}` : diff}
    </span>
  );
}

function DistBar({ dist }: { dist: number[] }) {
  const total = dist.reduce((s, c) => s + c, 0);
  if (total === 0) return <div className="h-3 rounded" style={{ background: "var(--bg-hover)" }} />;
  return (
    <div className="flex h-3 rounded overflow-hidden" style={{ background: "var(--bg-hover)" }}>
      {dist.map((c, i) =>
        c > 0 ? (
          <div
            key={i}
            title={`${i + 1}: ${c}`}
            style={{ width: `${(c / total) * 100}%`, background: SCALE_COLORS[i] }}
          />
        ) : null,
      )}
    </div>
  );
}

// ── Heatmap-Ansicht ──────────────────────────────────────────────────────────────

function HeatmapView({ round, team }: { round?: string; team?: string }) {
  const [segment, setSegment] = useState<"team" | "employee">("team");
  const [view, setView] = useState<"zustimmung" | "differenz">("zustimmung");
  const { data, isLoading } = useQuery({
    queryKey: ["survey-heatmap", segment, view, round, team],
    queryFn: () => surveyApi.getHeatmap(segment, view, round, team),
    staleTime: 5 * 60_000,
  });

  return (
    <section
      className="rounded-lg p-4"
      style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
    >
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Toggle
          label="Ansicht"
          value={view}
          options={[
            ["zustimmung", "Zustimmung"],
            ["differenz", "Differenz"],
          ]}
          onChange={setView}
        />
        <Toggle
          label="Segmentiert nach"
          value={segment}
          options={[
            ["team", "Team"],
            ["employee", "Mitarbeiter"],
          ]}
          onChange={setSegment}
        />
      </div>
      {data?.valueMode === "scale" && (
        <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>
          {view === "differenz"
            ? "Zellen zeigen die Abweichung des Skalenwerts (1–5) vom Durchschnitt aller Antworten."
            : "Zellen zeigen den echten Antwortwert auf der Skala 1–5 (Themenzeilen = Mittelwert der Fragen)."}
        </p>
      )}
      {isLoading && (
        <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
          <Loader2 size={16} className="animate-spin" /> Lade Heatmap …
        </div>
      )}
      {!isLoading && data?.available && (data.columns?.length ?? 0) === 0 && (
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          Keine Daten im aktuellen Filter.
        </p>
      )}
      {!isLoading && data?.available && (data.columns?.length ?? 0) > 0 && (
        <div className="overflow-x-auto">
          <table className="text-xs border-collapse">
            <thead>
              <tr style={{ color: "var(--text-muted)" }}>
                <th
                  className="text-left font-medium py-1.5 pr-3 sticky left-0 z-10"
                  style={{ background: "var(--bg-card)", minWidth: "16rem" }}
                >
                  Fragenthema
                </th>
                <th className="text-center font-medium py-1.5 px-2">Zustimmung</th>
                {data.columns!.map((col) => (
                  <th
                    key={col.key}
                    className="font-medium py-1.5 px-1 align-bottom"
                    style={{ minWidth: "3.5rem" }}
                  >
                    <div
                      className="whitespace-nowrap text-[10px]"
                      style={{
                        writingMode: "vertical-rl",
                        transform: "rotate(180deg)",
                        maxHeight: "8rem",
                      }}
                      title={col.label}
                    >
                      {col.label}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows!.map((row) => (
                <tr key={row.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <td
                    className={`py-1.5 pr-3 sticky left-0 z-10 ${row.isTheme ? "font-semibold" : "pl-5"}`}
                    style={{
                      background: "var(--bg-card)",
                      color: row.isTheme ? "var(--text-primary)" : "var(--text-secondary)",
                    }}
                  >
                    {row.label}
                  </td>
                  <td
                    className="py-1.5 px-2 text-center font-semibold"
                    style={{ color: pctColor(row.overall) }}
                  >
                    {fmtPct(row.overall)}
                  </td>
                  {data.columns!.map((col) => {
                    const v = row.cells[col.key] ?? null;
                    const scale = data.valueMode === "scale";
                    const bg =
                      view === "differenz"
                        ? scale
                          ? scaleDiffColor(v)
                          : diffColor(v)
                        : scale
                          ? scaleColor(v)
                          : pctColor(v);
                    const label =
                      view === "differenz"
                        ? scale
                          ? fmtScaleDiff(v)
                          : fmtDiff(v)
                        : scale
                          ? fmtScale(v)
                          : v;
                    return (
                      <td
                        key={col.key}
                        className="py-1.5 px-1 text-center"
                        style={{
                          background: bg,
                          color: v === null ? "var(--text-muted)" : "#1a1a1a",
                        }}
                      >
                        {v === null ? "–" : label}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Toggle<T extends string>({
  label,
  value,
  options,
  onChange,
}: { label: string; value: T; options: Array<[T, string]>; onChange: (v: T) => void }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs" style={{ color: "var(--text-muted)" }}>
        {label}:
      </span>
      <div className="flex rounded overflow-hidden" style={{ border: "1px solid var(--border)" }}>
        {options.map(([val, lbl]) => (
          <button
            type="button"
            key={val}
            onClick={() => onChange(val)}
            className="px-3 py-1 text-xs font-medium"
            style={{
              background: value === val ? "var(--accent)" : "transparent",
              color: value === val ? "#fff" : "var(--text-muted)",
            }}
          >
            {lbl}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Frage-Detail-Panel (rechts) ────────────────────────────────────────────────

function QuestionDrawer({
  qid,
  round,
  team,
  onClose,
}: { qid: string; round?: string; team?: string; onClose: () => void }) {
  const [tab, setTab] = useState<"uebersicht" | "kommentare">("uebersicht");
  const { data, isLoading } = useQuery({
    queryKey: ["survey-question", qid, round, team],
    queryFn: () => surveyApi.getQuestion(qid, round, team),
    staleTime: 5 * 60_000,
  });

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      style={{ background: "rgba(0,0,0,0.35)" }}
      onClick={onClose}
    >
      <div
        className="w-[26rem] max-w-[90vw] h-full overflow-y-auto p-5"
        style={{ background: "var(--bg-card)", borderLeft: "1px solid var(--border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <h2 className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>
            {data?.text ?? "Frage"}
          </h2>
          <button type="button" onClick={onClose} style={{ color: "var(--text-muted)" }}>
            <X size={18} />
          </button>
        </div>

        <div
          className="flex gap-4 mb-4 text-sm"
          style={{ borderBottom: "1px solid var(--border)" }}
        >
          {(["uebersicht", "kommentare"] as const).map((t) => (
            <button
              type="button"
              key={t}
              onClick={() => setTab(t)}
              className="pb-2 font-medium"
              style={{
                color: tab === t ? "var(--accent)" : "var(--text-muted)",
                borderBottom: tab === t ? "2px solid var(--accent)" : "2px solid transparent",
              }}
            >
              {t === "uebersicht"
                ? "Übersicht"
                : `Kommentare ${data ? `(${data.comments.length})` : ""}`}
            </button>
          ))}
        </div>

        {isLoading && (
          <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
            <Loader2 size={16} className="animate-spin" /> Lädt …
          </div>
        )}

        {!isLoading && data && tab === "uebersicht" && (
          <div>
            <div className="flex gap-6 mb-4 text-sm">
              <div>
                <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                  Antwortrate
                </div>
                <div className="font-bold" style={{ color: "var(--text-primary)" }}>
                  {data.answered} Antworten
                </div>
              </div>
              <div>
                <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                  Zustimmung
                </div>
                <div className="font-bold" style={{ color: pctColor(data.zustimmung) }}>
                  {fmtPct(data.zustimmung)}
                </div>
              </div>
            </div>
            <div className="text-xs font-semibold mb-2" style={{ color: "var(--text-muted)" }}>
              Antworten
            </div>
            <div className="space-y-1.5">
              {data.answers
                .slice()
                .reverse()
                .map((a) => (
                  <div key={a.value} className="flex items-center gap-2 text-sm">
                    <span
                      className="w-40 shrink-0 truncate"
                      style={{ color: "var(--text-secondary)" }}
                    >
                      {a.value}.{a.label ? ` ${a.label}` : ""}
                    </span>
                    <div
                      className="flex-1 h-3 rounded overflow-hidden"
                      style={{ background: "var(--bg-hover)" }}
                    >
                      <div
                        style={{
                          width: `${a.pct}%`,
                          background: SCALE_COLORS[a.value - 1],
                          height: "100%",
                        }}
                      />
                    </div>
                    <span className="w-16 text-right" style={{ color: "var(--text-muted)" }}>
                      {a.pct}% · {a.count}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {!isLoading && data && tab === "kommentare" && (
          <div className="space-y-3">
            {data.comments.length === 0 && (
              <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                Keine Kommentare.
              </p>
            )}
            {data.comments.map((cm, i) => (
              <div key={i} className="rounded p-3" style={{ background: "var(--bg-hover)" }}>
                <p className="text-sm mb-2" style={{ color: "var(--text-primary)" }}>
                  {cm.text}
                </p>
                <div className="flex items-center gap-2 text-xs">
                  {cm.answerValue != null && (
                    <span
                      className="px-1.5 py-0.5 rounded font-medium"
                      style={{ background: SCALE_COLORS[cm.answerValue - 1], color: "#1a1a1a" }}
                    >
                      {cm.answerValue}
                    </span>
                  )}
                  <span style={{ color: "var(--text-muted)" }}>{cm.name}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Upload (nur Admin) ──────────────────────────────────────────────────────────

function UploadButton({ onDone }: { onDone: () => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SurveyUploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingReplace, setPendingReplace] = useState<File | null>(null);

  async function doUpload(file: File, replace: boolean) {
    setBusy(true);
    setError(null);
    try {
      const res = await surveyApi.upload(file, { replace });
      setResult(res);
      setPendingReplace(null);
      onDone();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Upload fehlgeschlagen";
      if (msg.includes("existiert bereits")) setPendingReplace(file);
      else setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.xlsx"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) doUpload(f, false);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium"
        style={{ background: "var(--accent)", color: "#fff", opacity: busy ? 0.6 : 1 }}
      >
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Export
        hochladen
      </button>
      {(result || error || pendingReplace) && (
        <div
          className="absolute right-0 mt-2 w-80 rounded-lg p-4 z-50 shadow-lg text-sm"
          style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
        >
          {error && (
            <div className="flex items-start gap-2" style={{ color: "var(--risk-high-text)" }}>
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <div>{error}</div>
            </div>
          )}
          {pendingReplace && (
            <div>
              <div className="flex items-start gap-2 mb-3" style={{ color: "var(--text-primary)" }}>
                <AlertTriangle
                  size={16}
                  className="mt-0.5 shrink-0"
                  style={{ color: "var(--risk-medium-text)" }}
                />
                <div>Diese Runde existiert bereits. Vorhandene Daten ersetzen?</div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => doUpload(pendingReplace, true)}
                  className="px-3 py-1 rounded text-xs font-medium"
                  style={{ background: "var(--accent)", color: "#fff" }}
                >
                  Ersetzen
                </button>
                <button
                  type="button"
                  onClick={() => setPendingReplace(null)}
                  className="px-3 py-1 rounded text-xs"
                  style={{ border: "1px solid var(--border)", color: "var(--text-muted)" }}
                >
                  Abbrechen
                </button>
              </div>
            </div>
          )}
          {result && (
            <div>
              <div
                className="flex items-center gap-2 mb-2 font-semibold"
                style={{ color: "var(--text-primary)" }}
              >
                <CheckCircle2 size={16} style={{ color: "var(--risk-low-text)" }} /> Runde{" "}
                {result.round} {result.replaced ? "ersetzt" : "importiert"}
              </div>
              <ul className="space-y-1" style={{ color: "var(--text-secondary)" }}>
                <li>
                  Zugeordnet: <b>{result.mapped}</b>
                  {result.invited ? ` von ${result.invited} Eingeladenen` : ""}
                </li>
                {result.scaleObserved && (
                  <li>
                    Skala beobachtet: {result.scaleObserved.min}–{result.scaleObserved.max}
                  </li>
                )}
                {result.unresolvedCount > 0 && (
                  <li style={{ color: "var(--risk-medium-text)" }}>
                    {result.unresolvedCount} nicht zuordenbar:
                    <div
                      className="text-xs mt-1 max-h-24 overflow-y-auto"
                      style={{ color: "var(--text-muted)" }}
                    >
                      {result.unresolved.join(", ")}
                    </div>
                  </li>
                )}
              </ul>
              <button
                type="button"
                onClick={() => setResult(null)}
                className="mt-3 px-3 py-1 rounded text-xs"
                style={{ border: "1px solid var(--border)", color: "var(--text-muted)" }}
              >
                Schließen
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyState({ isAdmin }: { isAdmin: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <ClipboardList size={40} style={{ color: "var(--text-muted)" }} />
      <p className="mt-4 text-sm font-medium" style={{ color: "var(--text-primary)" }}>
        Noch keine Umfragedaten
      </p>
      <p className="mt-1 text-sm max-w-md" style={{ color: "var(--text-muted)" }}>
        {isAdmin
          ? "Lade den Personio-Puls-Export (CSV oder Excel) über den Button oben rechts hoch. Teilnehmer werden anonym zur Personio-ID aufgelöst."
          : "Sobald die erste Pulsumfrage ausgewertet ist, erscheinen hier die Ergebnisse deiner Berichtslinie."}
      </p>
    </div>
  );
}
